import React from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useCta } from "@/context/SettingsContext";
import { Btn, Eyebrow, Section, SectionTitle, Lead } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";
import { IMAGES } from "@/config/images";

const Card = ({ label, forWhom, benefits, image, dark, href, ctaLabel, testid }) => (
  <div
    className={`relative flex h-full flex-col overflow-hidden border ${
      dark ? "border-white/10 bg-navy text-white" : "border-navy/10 bg-white"
    }`}
    data-testid={testid}
  >
    <div className="relative h-44 overflow-hidden">
      <img src={image.src} alt={image.alt} loading="lazy" className="h-full w-full object-cover" />
      <div
        aria-hidden="true"
        className={`absolute inset-0 ${dark ? "bg-navy/70" : "bg-navy/45"} mix-blend-multiply`}
      />
      <span className="absolute bottom-5 left-6 font-display text-2xl font-semibold uppercase tracking-[0.06em] text-white">
        {label}
      </span>
    </div>
    <div className="flex flex-1 flex-col p-7 sm:p-9">
      <p className={`text-sm ${dark ? "text-white/60" : "text-slate-600"}`}>{forWhom}</p>
      <ul className="mt-7 space-y-3">
        {benefits.map((b) => (
          <li key={b} className="flex items-start gap-3">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-gold" : "text-grn"}`} strokeWidth={2.4} />
            <span className={`text-[13px] ${dark ? "text-white/80" : "text-navy/80"}`}>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-9">
        <Btn href={href || "#"} external variant={dark ? "gold" : "navy"} size="sm">
          {ctaLabel} <ArrowUpRight className="h-3.5 w-3.5" />
        </Btn>
      </div>
    </div>
  </div>
);

export const Compare = () => {
  const { t } = useI18n();
  const cta = useCta();
  const m = t("home.compare.member");
  const v = t("home.compare.vendor");

  return (
    <Section className="bg-slate-50/70 py-24 sm:py-32" data-testid="compare-section">
      <div className="max-w-3xl">
        <Reveal>
          <Eyebrow>{t("home.compare.eyebrow")}</Eyebrow>
          <SectionTitle className="mt-6">{t("home.compare.title")}</SectionTitle>
          <Lead className="mt-5">{t("home.compare.sub")}</Lead>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-8 lg:grid-cols-2">
        <Reveal>
          <Card
            testid="member-card"
            label={m.label}
            forWhom={m.for}
            benefits={m.benefits}
            image={IMAGES.meeting}
            href={cta.member_registration_url}
            ctaLabel={t("cta.joinMember")}
          />
        </Reveal>
        <Reveal delay={0.12}>
          <Card
            testid="vendor-card"
            dark
            label={v.label}
            forWhom={v.for}
            benefits={v.benefits}
            image={IMAGES.logistics}
            href={cta.vendor_registration_url}
            ctaLabel={t("cta.registerVendor")}
          />
        </Reveal>
      </div>
    </Section>
  );
};
